/**
 * React Hook for Activity Logging
 * Provides easy access to centralized activity logging for all components
 */

import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../contexts/UserContext';
import activityLogger from '../services/activityLogger';

export const useActivityLogger = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { userData } = useUser();

  // Generic activity logger
  const logActivity = useCallback(async (functionPage, searchParameters) => {
    return activityLogger.logActivity({
      functionPage,
      searchParameters,
      auth: { getAccessTokenSilently, user },
      userData
    });
  }, [getAccessTokenSilently, user, userData]);

  // Convenience methods for specific tabs
  const logSearchLoads = useCallback(async (searchParameters) => {
    return activityLogger.logSearchLoads(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logLanePricing = useCallback(async (searchParameters) => {
    return activityLogger.logLanePricing(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logRateInsights = useCallback(async (searchParameters) => {
    return activityLogger.logRateInsights(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logBrokerMetrics = useCallback(async (searchParameters) => {
    return activityLogger.logBrokerMetrics(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logRouteOptimization = useCallback(async (searchParameters) => {
    return activityLogger.logRouteOptimization(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logHeatMap = useCallback(async (searchParameters) => {
    return activityLogger.logHeatMap(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  const logFreightAI = useCallback(async (searchParameters) => {
    return activityLogger.logFreightAI(searchParameters, { getAccessTokenSilently, user }, userData);
  }, [getAccessTokenSilently, user, userData]);

  return {
    logActivity,
    logSearchLoads,
    logLanePricing,
    logRateInsights,
    logBrokerMetrics,
    logRouteOptimization,
    logHeatMap,
    logFreightAI
  };
};

export default useActivityLogger;